/**
 * useBonusAddons — lightweight hook that fetches /bonus-addons/mine and
 * exposes helper functions so tool buttons can reflect purchased add-on
 * credits alongside the tier-based capability check.
 *
 * Usage:
 *   const { hasCredit, expiresAt } = useBonusAddons();
 *   const unlocked = can('bulk_draft_novel') || hasCredit('bulk_draft');
 */
import { useEffect, useState, useCallback } from 'react';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

async function fetchMine() {
  const r = await fetch(`${API}/bonus-addons/mine`, { credentials: 'include' });
  if (!r.ok) return {};
  const d = await r.json();
  return d?.grants || {};
}

export function useBonusAddons() {
  const [grants, setGrants] = useState(null); // null = loading

  const load = useCallback(async () => {
    try {
      setGrants(await fetchMine());
    } catch {
      setGrants({});
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Refresh whenever a purchase completes in any tab.
  useEffect(() => {
    window.addEventListener('loom:addon-credited', load);
    return () => window.removeEventListener('loom:addon-credited', load);
  }, [load]);

  /**
   * hasCredit(field) — true when the user has ≥ 1 consumable credit for
   * the given grants field (e.g. 'bulk_draft', 'continuity_audit').
   */
  const hasCredit = useCallback(
    (field) => {
      if (!grants) return false;
      const v = grants[field];
      if (typeof v === 'number') return v > 0;
      if (typeof v === 'object' && v !== null) return (v.remaining ?? 0) > 0;
      return false;
    },
    [grants],
  );

  /**
   * expiresAt(field) — ISO string for duration-pass expiry, or null.
   * e.g. expiresAt('adobe_canva_pass')
   */
  const expiresAt = useCallback(
    (field) => grants?.[field]?.expires_at ?? null,
    [grants],
  );

  /**
   * isPassActive(field) — true when a duration pass has not yet expired.
   */
  const isPassActive = useCallback(
    (field) => {
      const exp = grants?.[field]?.expires_at;
      if (!exp) return false;
      return new Date(exp) > new Date();
    },
    [grants],
  );

  return { grants, hasCredit, expiresAt, isPassActive, loading: grants === null };
}
